import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import { logError } from '@/commons/logger';
import { searchClient } from '@/components/essentials/AlgoliaSearchClient';
import { GET_CUSTOM_LIST_PRODUCT_DATA_V2 } from '@/graphql/queries';
import addDerivedProductData from '@/hooks/useProductListingData/helpers/addDerivedProductData';
import useProductListingWithFavorites from '@/hooks/useProductListingData/useProductListingWithFavorites';
import useProductVariantsToShow from '@/hooks/useProductListingData/useProductVariantsToShow';
import buildCustomListCards from './buildCustomListCards';
import buildCustomListProductListInput from './buildCustomListProductListInput';
import buildCustomListRequest from './buildCustomListRequest';
import { CUSTOM_LIST_INDEX_NAME } from './constants';
import expandCustomListHits from './expandCustomListHits';
const EMPTY_HITS = [];
const EMPTY_PRODUCTS = [];
const useAlgoliaCustomProductList = t0 => {
  const $ = _c(33);
  const {
    listId,
    filterByStock: t1,
    skip: t2
  } = t0;
  const filterByStock = t1 === undefined ? false : t1;
  const skip = t2 === undefined ? false : t2;
  const [searchResult, setSearchResult] = useState(null);
  let t3;
  let t4;
  if ($[0] !== listId || $[1] !== skip) {
    t3 = () => {
      if (skip || !listId) {
        return;
      }
      let cancelled = false;
      searchClient.search([buildCustomListRequest(listId)]).then(response => {
        if (cancelled) {
          return;
        }
        const result = response?.results?.[0];
        setSearchResult({
          listId,
          hits: result?.hits || EMPTY_HITS,
          queryID: result?.queryID || ""
        });
      }).catch(error => {
        if (cancelled) {
          return;
        }
        logError(error instanceof Error ? error : new Error(String(error)), "Custom list Algolia search failed", {
          listId
        });
        setSearchResult({
          listId,
          hits: EMPTY_HITS,
          queryID: ""
        });
      });
      return () => {
        cancelled = true;
      };
    };
    t4 = [listId, skip];
    $[0] = listId;
    $[1] = skip;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  useEffect(t3, t4);
  const settled = !skip && searchResult?.listId === listId ? searchResult : null;
  const hits = settled?.hits || EMPTY_HITS;
  const queryID = settled?.queryID || "";
  const algoliaLoading = !skip && Boolean(listId) && !settled;
  let t5;
  if ($[4] !== hits || $[5] !== listId) {
    t5 = expandCustomListHits(hits, listId);
    $[4] = hits;
    $[5] = listId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const items = t5;
  let t6;
  if ($[7] !== items) {
    t6 = buildCustomListProductListInput(items);
    $[7] = items;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const productList = t6;
  const priceQuerySkipped = skip || !listId || algoliaLoading || productList.length === 0;
  let t7;
  if ($[9] !== filterByStock || $[10] !== productList) {
    t7 = {
      productList,
      filterByStock,
      limit: -1
    };
    $[9] = filterByStock;
    $[10] = productList;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== priceQuerySkipped || $[13] !== t7) {
    t8 = {
      skip: priceQuerySkipped,
      fetchPolicy: "cache-and-network",
      variables: t7
    };
    $[12] = priceQuerySkipped;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const {
    data,
    loading: priceLoading
  } = useQuery(GET_CUSTOM_LIST_PRODUCT_DATA_V2, t8);
  const listing = data?.getCustomProductListDataV2;
  const t9 = listing?.products || EMPTY_PRODUCTS;
  let t10;
  if ($[15] !== listing?.upholsteryOptions || $[16] !== t9) {
    let t11;
    if ($[18] !== listing?.upholsteryOptions) {
      t11 = product => addDerivedProductData({
        product,
        upholsteryOptions: listing?.upholsteryOptions
      });
      $[18] = listing?.upholsteryOptions;
      $[19] = t11;
    } else {
      t11 = $[19];
    }
    t10 = t9.map(t11);
    $[15] = listing?.upholsteryOptions;
    $[16] = t9;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const pricedProducts = t10;
  let t11;
  if ($[20] !== pricedProducts || $[21] !== skip) {
    t11 = {
      products: pricedProducts,
      skipClearanceQuery: skip
    };
    $[20] = pricedProducts;
    $[21] = skip;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  const productsWithVariants = useProductVariantsToShow(t11);
  let t12;
  if ($[23] !== productsWithVariants) {
    t12 = {
      products: productsWithVariants
    };
    $[23] = productsWithVariants;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  const productsWithFavorites = useProductListingWithFavorites(t12);
  let t13;
  if ($[25] !== items || $[26] !== productsWithFavorites || $[27] !== queryID) {
    t13 = buildCustomListCards({
      items,
      products: productsWithFavorites,
      queryID,
      indexName: CUSTOM_LIST_INDEX_NAME
    });
    $[25] = items;
    $[26] = productsWithFavorites;
    $[27] = queryID;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  const products = t13;
  const t14 = skip ? EMPTY_PRODUCTS : products;
  const t15 = skip ? false : algoliaLoading || !priceQuerySkipped && priceLoading && !data;
  const t16 = listId || "";
  let t17;
  if ($[29] !== t14 || $[30] !== t15 || $[31] !== t16) {
    t17 = {
      products: t14,
      productsToPreload: EMPTY_PRODUCTS,
      loading: t15,
      isAllDataLoading: false,
      filterStateKey: t16,
      loadAllProducts: null
    };
    $[29] = t14;
    $[30] = t15;
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  return t17;
};
export default useAlgoliaCustomProductList;